import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface ButtonProps {
    text: string;
    onClick?: () => void;
    href?: string;
    variant?: 'primary' | 'secondary';
    icon?: boolean;
}

const StyledButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
    font-size: ${({ theme }) => theme.fontSizes.button};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.lg}`};
    border-radius: ${({ theme }) => theme.borderRadius.button};
    border: 2px solid ${({ theme }) => theme.colors.primary};
    background-color: ${({ $variant, theme }) =>
        $variant === 'primary' ? theme.colors.primary : 'transparent'};
    color: ${({ $variant, theme }) =>
        $variant === 'primary' ? theme.colors.text : theme.colors.primary};
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.xs};
    text-decoration: none;

    &:hover {
        background-color: ${({ $variant, theme }) =>
            $variant === 'primary' ? theme.colors.primaryDark : theme.colors.primary};
        color: ${({ theme }) => theme.colors.text};
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }
`;

const Arrow = styled.span`
    display: inline-block;
    transition: transform 0.2s ease;

    ${StyledButton}:hover & {
        transform: translateX(4px);
    }
`;

export const Button: React.FC<ButtonProps> = ({
    text,
    onClick,
    href,
    variant = 'primary',
    icon = true,
}) => {
    const content = (
        <>
            {text}
            {icon && <Arrow>→</Arrow>}
        </>
    );

    // If href is provided and it's an internal route, use Link with onClick
    if (href && href.startsWith('/')) {
        return (
            <StyledButton as={Link} to={href} onClick={onClick} $variant={variant}>
                {content}
            </StyledButton>
        );
    }

    // If href is external, use regular anchor tag
    if (href) {
        return (
            <StyledButton
                as="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                $variant={variant}
            >
                {content}
            </StyledButton>
        );
    }

    // If no href, use button with onClick
    return (
        <StyledButton onClick={onClick} $variant={variant}>
            {content}
        </StyledButton>
    );
};
